'use client';

import { Fragment, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams, usePathname } from 'next/navigation';
import { HeaderProps } from '@/components/authorable/site-structure/Header/Header';
import { ILink, IStandardContentPage } from '@/.generated';
import { DEFAULT_LOCALE } from '@/constants/locales';
import { cleanPageTitle, usePageReferenceLink } from '@/lib/hooks/usePageReferenceUrl';
import { getEntryWithFields } from '@/lib/contentstack/entries';
import { isLanguageSupported } from '@/lib/contentstack/language';
import { breadcrumbVariants } from './Breadcrumb.styles';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { JsonLdScript } from '@/helpers/Seo/JsonLdScript';
import { trackAdobeClick } from '@/utils/adobe';

// Generic tree node used to flatten the multi-level Header nav for traversal.
interface NavNode {
  title?: string;
  href?: string;
  pageRefUid?: string;
  pageRefContentType?: string;
  children: NavNode[];
}

// Resolve element types out of the IHeader nav. In V2 the deeper levels are
// nested per-link: each L2 link owns its L3 groups, each L3 link owns its L4 groups.
type L1Item = NonNullable<HeaderProps['nav_level_one']>[number];
type L1Column = NonNullable<L1Item['nav_columns']>[number];
type L2Item = NonNullable<L1Column['nav_level_two']>[number];
type L2Link = NonNullable<L2Item['navigation_level_two_links']>[number];
type L3Item = NonNullable<L2Link['navigation_level_three']>[number];
type L3Link = NonNullable<L3Item['navigation_level_three_links']>[number];

// Minimal link shape the breadcrumb reads — covers INavigationLink and every
// inline link object nested inside IHeader's navigation levels.
type NavLink = {
  link?: ILink;
  page_reference?: { uid?: string; _content_type_uid?: string }[];
};

// Convert a nav link into a leaf NavNode, skipping entries without a label.
const toLeafNode = (link: NavLink): NavNode => {
  const title = link?.link?.title?.trim();
  const href = link?.link?.href || undefined;
  const pageRef = link?.page_reference?.[0];

  return {
    title,
    href,
    pageRefUid: pageRef?.uid,
    pageRefContentType: pageRef?._content_type_uid,
    children: [],
  };
};

// Push every valid leaf from a links array into the parent's children list.
// NOTE: hide_from_main_navigation is intentionally NOT filtered here — those links
// must remain visible in the breadcrumb even when hidden from the Header's mega menu.
const appendLeafLinks = (parent: NavNode, links?: NavLink[]) => {
  links?.forEach((link) => {
    if (link) parent.children.push(toLeafNode(link));
  });
};

// Build a NavNode for a link that may itself nest a deeper level of groups,
// resolving each group's links (and their groups) into the link node's children.
const buildLinkNode = <Link extends NavLink>(
  link: Link,
  groups: { title?: string; links?: NavLink[] }[]
): NavNode => {
  const linkNode = toLeafNode(link);
  groups.forEach((group) => {
    const groupNode: NavNode = { title: group.title?.trim() || '', children: [] };
    appendLeafLinks(groupNode, group.links);
    linkNode.children.push(groupNode);
  });
  return linkNode;
};

// Build a unified tree from Header's nav_level_one. V2 nests deeper levels under
// individual links, so each link becomes a node that also carries its sub-groups.
const buildNavTree = (navLevelOne: HeaderProps['nav_level_one']): NavNode[] => {
  if (!navLevelOne?.length) return [];
  return navLevelOne.map((l1) => {
    const l1Node: NavNode = { title: l1?.nav_level_one_title?.trim() || '', children: [] };
    const columns = (l1?.nav_columns ?? []) as L1Column[];
    columns.forEach((column) => {
      // L1 leaf links sit directly under the L1 category.
      appendLeafLinks(l1Node, column?.nav_level_one_links);
      const l2List = (column?.nav_level_two ?? []) as L2Item[];
      l2List.forEach((l2) => {
        const l2Node: NavNode = { title: l2?.nav_level_two_title?.trim() || '', children: [] };
        const l2Links = (l2?.navigation_level_two_links ?? []) as L2Link[];
        l2Links.forEach((l2Link) => {
          // Each L3 group nests its L4 groups under its individual L3 links.
          const l3Groups = (l2Link?.navigation_level_three ?? []) as L3Item[];
          const l3Nodes: NavNode[] = l3Groups.map((l3) => {
            const l3Node: NavNode = {
              title: l3?.navigation_level_three_title?.trim() || '',
              children: [],
            };
            const l3Links = (l3?.navigation_level_three_links ?? []) as L3Link[];
            l3Links.forEach((l3Link) => {
              // EXTEND HERE — when navigation_level_five is added, walk it under each L4 group.
              const l4Groups = (l3Link?.navigation_level_four ?? []).map((l4) => ({
                title: l4?.navigation_level_four_title,
                links: l4?.navigation_level_four_links,
              }));
              l3Node.children.push(buildLinkNode(l3Link, l4Groups));
            });
            return l3Node;
          });
          const l2LinkNode = toLeafNode(l2Link);
          l2LinkNode.children.push(...l3Nodes);
          l2Node.children.push(l2LinkNode);
        });
        l1Node.children.push(l2Node);
      });
    });
    return l1Node;
  });
};

// Strip query, hash, locale prefix, and trailing slash so URLs compare cleanly.
const normalizeHref = (href: string | undefined, locale: string): string => {
  if (!href) return '';
  let path = href.split('?')[0].split('#')[0];
  const localePrefix = `/${locale}`;
  if (locale !== DEFAULT_LOCALE && (path === localePrefix || path.startsWith(`${localePrefix}/`))) {
    path = path.slice(localePrefix.length) || '/';
  }
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  return path || '/';
};

// Criteria used to identify the current page within the nav tree.
interface MatchTarget {
  pageUid?: string;
  pageContentTypeUid?: string;
  pathname: string;
  locale: string;
}

// Decide whether a NavNode represents the current page.
const isCurrentPageNode = (node: NavNode, target: MatchTarget): boolean => {
  // Prefer page_reference uid matching since most CMS links use it.
  if (target.pageUid && node.pageRefUid && node.pageRefUid === target.pageUid) {
    if (!target.pageContentTypeUid || !node.pageRefContentType) return true;
    return node.pageRefContentType === target.pageContentTypeUid;
  }
  // Fall back to href comparison for plain-link entries (e.g. SRP Monthly Service Charge).
  if (node.href && normalizeHref(node.href, target.locale) === target.pathname) return true;
  return false;
};

// Depth-first search that returns the ancestor trail of the node matching the current page.
const findTrail = (
  nodes: NavNode[],
  target: MatchTarget,
  trail: NavNode[] = []
): NavNode[] | null => {
  for (const node of nodes) {
    const nextTrail = [...trail, node];
    if (isCurrentPageNode(node, target)) return nextTrail;
    const deeper = findTrail(node.children, target, nextTrail);
    if (deeper) return deeper;
  }
  return null;
};

// Determine whether the user is on the locale's home page (no breadcrumb to show).
const isHomePath = (pathname: string, locale: string): boolean => {
  if (pathname === '/' || pathname === '') return true;
  if (locale !== DEFAULT_LOCALE && pathname === `/${locale}`) return true;
  return false;
};

// Resolve each trail node's display label (link title, else the referenced page title,
// trimmed at "|"). Used to collapse consecutive segments that render the same text.
const useTrailLabels = (trail: NavNode[]): string[] => {
  const params = useParams();
  const localeParam = params?.locale as string | undefined;
  const language = localeParam && isLanguageSupported(localeParam) ? localeParam : DEFAULT_LOCALE;
  const [labels, setLabels] = useState<string[]>(() => trail.map((node) => node.title ?? ''));

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      const resolved = await Promise.all(
        trail.map(async (node) => {
          if (node.title) return node.title;
          if (!node.pageRefUid || !node.pageRefContentType) return '';
          const entry = await getEntryWithFields<{ title?: string }>(
            node.pageRefUid,
            node.pageRefContentType,
            ['title'],
            language
          );
          return cleanPageTitle(entry?.title) ?? '';
        })
      );
      if (!cancelled) setLabels(resolved);
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [trail, language]);

  return labels;
};

// One resolved crumb going into the BreadcrumbList schema — `item` is omitted when
// no URL is known (schema.org allows a URL-less ListItem, notably for the last one).
interface BreadcrumbSchemaItem {
  name: string;
  item?: string;
}

// Builds a schema.org BreadcrumbList payload from the visible breadcrumb trail.
const buildBreadcrumbListSchema = (items: BreadcrumbSchemaItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((entry, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: entry.name,
    ...(entry.item && { item: entry.item }),
  })),
});

// Single trail entry — resolves page_reference URLs the same way Header nav links do.
const BreadcrumbItem = ({ node, isLast }: { node: NavNode; isLast: boolean }) => {
  const { item, itemLink } = breadcrumbVariants();

  // Build a minimal IPage[] only when the node carries a page reference; memoized
  // so the effect inside usePageReferenceLink doesn't refire on every render.
  const pageRefArray = useMemo<IStandardContentPage[] | undefined>(() => {
    if (!node.pageRefUid || !node.pageRefContentType) return undefined;
    return [
      {
        uid: node.pageRefUid,
        _content_type_uid: node.pageRefContentType,
      } as unknown as IStandardContentPage,
    ];
  }, [node.pageRefUid, node.pageRefContentType]);

  // Mirrors NavigationLinkWrapper: resolve href + title from the page reference, so
  // page-reference-only links (empty link.title) still get a label.
  const { href: resolvedHref, title: resolvedTitle } = usePageReferenceLink(
    pageRefArray,
    node.href,
    node.title
  );
  const isLink = Boolean(resolvedHref);
  const label = node.title || resolvedTitle;

  return (
    <li className={item()}>
      {isLink ? (
        <Link
          href={resolvedHref as string}
          className={itemLink()}
          aria-current={isLast ? 'page' : undefined}
          onClick={(e) =>
            trackAdobeClick(
              { clickName: label || 'Breadcrumb', clickType: 'breadcrumb-link', clickLocation: 'breadcrumb' },
              e
            )
          }
        >
          {label}
        </Link>
      ) : (
        <span aria-current={isLast ? 'page' : undefined}>{label}</span>
      )}
    </li>
  );
};

export const Breadcrumb = (props: HeaderProps) => {
  const pathname = usePathname() || '/';
  const params = useParams();
  const currentLocale = (params?.locale as string | undefined) ?? DEFAULT_LOCALE;

  // Parse the nav once per Header payload, not on every render.
  const tree = useMemo(() => buildNavTree(props.nav_level_one), [props.nav_level_one]);

  // Locate the current page within the nav tree using pageUid first, pathname as fallback.
  const trail = useMemo(() => {
    const target: MatchTarget = {
      pageUid: props.pageUid,
      pageContentTypeUid: props.pageContentTypeUid,
      pathname: normalizeHref(pathname, currentLocale),
      locale: currentLocale,
    };
    return findTrail(tree, target) ?? [];
  }, [tree, pathname, currentLocale, props.pageUid, props.pageContentTypeUid]);

  // Drop ancestors with blank titles so empty CMS category labels don't render, but
  // keep page-reference nodes — their label resolves from the referenced page title.
  const visibleTrail = useMemo(
    () => trail.filter((node) => (node.title && node.title.trim().length > 0) || !!node.pageRefUid),
    [trail]
  );

  // Resolved labels (called before early returns to satisfy the Rules of Hooks).
  const trailLabels = useTrailLabels(visibleTrail);

  const {
    globalLabels: { breadcrumb_home_segment_label },
  } = useGlobalLabels();

  // Hide on the home page or when the current URL isn't reachable from the nav.
  if (isHomePath(pathname, currentLocale) || trail.length === 0) return null;
  if (visibleTrail.length === 0) return null;

  // Collapse consecutive segments that resolve to the same label (e.g. an L4 group
  // whose title matches its only child page), keeping the deeper (current-page) node.
  // The resolved label rides along so the JSON-LD below mirrors exactly what renders —
  // page-reference-only nodes have no node.title and would otherwise be dropped from the schema.
  const displayTrail = visibleTrail
    .map((node, i) => ({ node, label: trailLabels[i] ?? '' }))
    .filter((_, i) => !(trailLabels[i] && trailLabels[i] === trailLabels[i + 1]));

  const homeHref = currentLocale === DEFAULT_LOCALE ? '/' : `/${currentLocale}`;
  const { wrapper, container, nav, list, homeLink, separator } = breadcrumbVariants();

  // Absolute site origin for schema URLs — JSON-LD requires fully qualified links.
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000';
  const homeLabel = breadcrumb_home_segment_label || 'Home';

  // Mirrors what's actually rendered below, so the schema never claims a segment the
  // page doesn't visibly show — nor omits one that it does.
  const breadcrumbSchemaItems: BreadcrumbSchemaItem[] = [
    { name: homeLabel, item: `${baseUrl}${homeHref}` },
    ...displayTrail
      .filter(({ label }) => label)
      .map(({ node, label }) => ({
        name: label,
        item: node.href ? `${baseUrl}${node.href}` : undefined,
      })),
  ];

  return (
    <div id="breadcrumb" className={wrapper()}>
      {breadcrumbSchemaItems.length > 0 && (
        <JsonLdScript schema={buildBreadcrumbListSchema(breadcrumbSchemaItems)} />
      )}
      <div className={container()}>
        <nav aria-label="Breadcrumb" className={nav()}>
          <ol className={list()}>
            <li>
              <Link
                href={homeHref}
                className={homeLink()}
                onClick={(e) =>
                  trackAdobeClick(
                    { clickName: homeLabel, clickType: 'breadcrumb-link', clickLocation: 'breadcrumb' },
                    e
                  )
                }
              >
                {homeLabel}
              </Link>
            </li>
            {/* Separator between Home and the first trail item */}
            <li aria-hidden="true" role="presentation">
              <span className={separator()}>/</span>
            </li>
            {displayTrail.map(({ node, label }, idx) => (
              <Fragment key={`${label}-${idx}`}>
                {/* Separator only between trail items, never before the first */}
                {idx > 0 && (
                  <li aria-hidden="true" role="presentation">
                    <span className={separator()}>/</span>
                  </li>
                )}
                <BreadcrumbItem node={node} isLast={idx === displayTrail.length - 1} />
              </Fragment>
            ))}
          </ol>
        </nav>
      </div>
    </div>
  );
};
